/**
 * Extracted Recipe Preview Component
 *
 * Displays and allows editing of extracted recipe data before import.
 * Includes collapsible sections for different recipe attributes.
 */

import { useState } from 'react';
import type { ExtractedRecipe } from '@/types/recipeImport';
import { BasicInfoSection } from './BasicInfoSection';
import { TimeServingsSection } from './TimeServingsSection';
import { StorageSection } from './StorageSection';
import { InstructionsSection } from './InstructionsSection';

interface ExtractedRecipePreviewProps {
  recipe: ExtractedRecipe;
  onChange: (recipe: ExtractedRecipe) => void;
}

export function ExtractedRecipePreview({ recipe, onChange }: ExtractedRecipePreviewProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="extracted-preview">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            className="w-5 h-5 text-green-600 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-left">
            <h3 className="text-base font-semibold text-gray-900">
              Recipe Extracted Successfully
            </h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Review and edit the extracted recipe details below
            </p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-6">
          {/* Basic Information */}
          <section>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
              Basic Information
            </h4>
            <BasicInfoSection recipe={recipe} onChange={onChange} />
          </section>

          {/* Time & Servings */}
          <section>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
              Time & Servings
            </h4>
            <TimeServingsSection recipe={recipe} onChange={onChange} />
          </section>

          {/* Storage Information */}
          <section>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
              Storage Information
            </h4>
            <StorageSection recipe={recipe} onChange={onChange} />
          </section>

          {/* Instructions */}
          <section>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
              Instructions
            </h4>
            <InstructionsSection recipe={recipe} onChange={onChange} />
          </section>
        </div>
      )}
    </div>
  );
}
